import { FC, useState } from 'react'
import { Button, Input, InputNumber, Space } from 'antd'
import { SaveOutlined, CloseOutlined } from '@ant-design/icons'
import { Formik, Form, Field, FieldProps } from 'formik'
import {
  ICustomerPricingFormValues,
  customerPricingValidationSchema,
} from '../interfaces'

// CSS for financial input styling
const financialInputStyles = `
  .financial-input .ant-input-number-input {
    text-align: right !important;
  }
`

interface IInlineCreateRowProps {
  onSave: (formData: ICustomerPricingFormValues) => Promise<void>
  onCancel: () => void
  loading?: boolean
}

export const InlineCreateRow: FC<IInlineCreateRowProps> = ({
  onSave,
  onCancel,
  loading = false,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (values: ICustomerPricingFormValues) => {
    setIsSubmitting(true)
    try {
      await onSave(values)
    } finally {
      setIsSubmitting(false)
    }
  }

  const initialValues: ICustomerPricingFormValues = {
    name: '',
    largeIceCubePriceTHB: 0,
    smallIceCubePriceTHB: 0,
    crushedIcePriceTHB: 0,
  }

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: financialInputStyles }} />
      <div className='bg-green-50 p-3 rounded-lg border-2 border-green-200'>
      <div className='mb-1.5'>
        <h3 className='text-sm font-medium text-green-800'>Add New Customer</h3>
      </div>
      <Formik
        initialValues={initialValues}
        validationSchema={customerPricingValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ setFieldValue, errors, touched, isValid }) => (
          <Form>
            {/* Form Fields */}
            <div className='grid grid-cols-4 gap-2 items-start mb-3'>
              {/* Name Field */}
              <div className='col-span-1'>
                <label className='block text-xs font-medium text-gray-700 mb-0.5'>
                  Customer Name *
                </label>
                <Field name='name'>
                  {({ field }: FieldProps) => (
                    <Input
                      {...field}
                      placeholder='Customer Name'
                      disabled={loading || isSubmitting}
                      status={errors.name && touched.name ? 'error' : ''}
                      size='small'
                    />
                  )}
                </Field>
                {errors.name && touched.name && (
                  <div className='text-red-500 text-xs mt-0.5'>{errors.name}</div>
                )}
              </div>

              {/* Large Ice Price */}
              <div className='col-span-1'>
                <label className='block text-xs font-medium text-gray-700 mb-0.5'>
                  ใหญ่ *
                </label>
                <Field name='largeIceCubePriceTHB'>
                  {({ field }: FieldProps) => (
                    <InputNumber
                      id={field.name}
                      name={field.name}
                      value={field.value}
                      onBlur={field.onBlur}
                      placeholder='0.00'
                      min={0}
                      max={1000000}
                      step={0.01}
                      precision={2}
                      className='financial-input'
                      style={{ width: '100%' }}
                      size='small'
                      disabled={loading || isSubmitting}
                      status={
                        errors.largeIceCubePriceTHB &&
                        touched.largeIceCubePriceTHB
                          ? 'error'
                          : ''
                      }
                      addonBefore='฿'
                      formatter={value =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                      parser={value => value!.replace(/\$\s?|(,*)/g, '')}
                      onChange={value =>
                        setFieldValue('largeIceCubePriceTHB', value ?? '')
                      }
                      onKeyDown={(e) => {
                        // Allow only numbers, decimal point, and control keys
                        if (!/[0-9.]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                          e.preventDefault()
                        }
                      }}
                    />
                  )}
                </Field>
                {errors.largeIceCubePriceTHB &&
                  touched.largeIceCubePriceTHB && (
                    <div className='text-red-500 text-xs mt-0.5'>
                      {errors.largeIceCubePriceTHB}
                    </div>
                  )}
              </div>

              {/* Small Ice Price */}
              <div className='col-span-1'>
                <label className='block text-xs font-medium text-gray-700 mb-0.5'>
                  เล็ก *
                </label>
                <Field name='smallIceCubePriceTHB'>
                  {({ field }: FieldProps) => (
                    <InputNumber
                      id={field.name}
                      name={field.name}
                      value={field.value}
                      onBlur={field.onBlur}
                      placeholder='0.00'
                      min={0}
                      max={1000000}
                      step={0.01}
                      precision={2}
                      className='financial-input'
                      style={{ width: '100%' }}
                      size='small'
                      disabled={loading || isSubmitting}
                      status={
                        errors.smallIceCubePriceTHB &&
                        touched.smallIceCubePriceTHB
                          ? 'error'
                          : ''
                      }
                      addonBefore='฿'
                      formatter={value =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                      parser={value => value!.replace(/\$\s?|(,*)/g, '')}
                      onChange={value =>
                        setFieldValue('smallIceCubePriceTHB', value ?? '')
                      }
                      onKeyDown={(e) => {
                        // Allow only numbers, decimal point, and control keys
                        if (!/[0-9.]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                          e.preventDefault()
                        }
                      }}
                    />
                  )}
                </Field>
                {errors.smallIceCubePriceTHB &&
                  touched.smallIceCubePriceTHB && (
                    <div className='text-red-500 text-xs mt-0.5'>
                      {errors.smallIceCubePriceTHB}
                    </div>
                  )}
              </div>

              {/* Crushed Ice Price */}
              <div className='col-span-1'>
                <label className='block text-xs font-medium text-gray-700 mb-0.5'>
                  โม่ *
                </label>
                <Field name='crushedIcePriceTHB'>
                  {({ field }: FieldProps) => (
                    <InputNumber
                      id={field.name}
                      name={field.name}
                      value={field.value}
                      onBlur={field.onBlur}
                      placeholder='0.00'
                      min={0}
                      max={1000000}
                      step={0.01}
                      precision={2}
                      className='financial-input'
                      style={{ width: '100%' }}
                      size='small'
                      disabled={loading || isSubmitting}
                      status={
                        errors.crushedIcePriceTHB && touched.crushedIcePriceTHB
                          ? 'error'
                          : ''
                      }
                      addonBefore='฿'
                      formatter={value =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                      parser={value => value!.replace(/\$\s?|(,*)/g, '')}
                      onChange={value =>
                        setFieldValue('crushedIcePriceTHB', value ?? '')
                      }
                      onKeyDown={(e) => {
                        // Allow only numbers, decimal point, and control keys
                        if (!/[0-9.]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                          e.preventDefault()
                        }
                      }}
                    />
                  )}
                </Field>
                {errors.crushedIcePriceTHB && touched.crushedIcePriceTHB && (
                  <div className='text-red-500 text-xs mt-0.5'>
                    {errors.crushedIcePriceTHB}
                  </div>
                )}
              </div>

              {/* Empty space */}
              <div className='col-span-1'></div>
            </div>

            {/* Action Buttons - Separate row, far right */}
            <div className='flex justify-end'>
              <Space size='small' className='gap-2'>
                <Button
                  icon={<CloseOutlined />}
                  size='small'
                  onClick={onCancel}
                  disabled={isSubmitting || loading}
                >
                  Cancel
                </Button>
                <Button
                  type='primary'
                  icon={<SaveOutlined />}
                  size='small'
                  htmlType='submit'
                  loading={isSubmitting}
                  disabled={!isValid || loading}
                >
                  Create
                </Button>
              </Space>
            </div>
          </Form>
        )}
      </Formik>
      </div>
    </>
  )
}
